/* eslint-disable react/prop-types */
import { useState, useEffect, useRef } from 'react';
import Moment from 'moment';
import generatePDF from 'react-to-pdf';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import Report from '../../../../Components/Report/Report';
import Module from './Module';
import AccountService from '../../../../Services/AccountService';
import XpektorButton from '../../../../Components/XpektorButton/XpektorButton';
import LoadingBar from '../../../../Components/LoadingBar/LoadingBar';

const gT = (t, key) => t(`company-info-modules.reports${key}`);

const RenderBalanceValues = (values) => {
  return [
    values.balanceIn,
    values.balanceIn,
    values.balanceOut - values.balanceIn,
    values.balanceOut,
  ];
};

function BalanceReportSubsection({ section, country }) {
  return section.accounts.length ? (
    <div className={country === 'NO' ? 'tabbed' : ''}>
      <Report.TableRow title={section.name} bold={country === 'NO'} />
      <Report.ListSection country={country}>
        {section.accounts.map((account) => {
          return (
            <Report.TableRow
              number={account.id}
              title={account.name}
              values={RenderBalanceValues(account)}
              key={account.id}
            />
          );
        })}
      </Report.ListSection>
      <Report.TableRow
        title={section.sum.name}
        values={RenderBalanceValues(section.sum)}
        bold={country === 'NO'}
        marginBottom={country !== 'NO'}
      />
    </div>
  ) : (
    ''
  );
}

function BalanceReportSection({ section, country, transformToUpper }) {
  return (
    <>
      <Report.TableRow
        title={transformToUpper ? section.name.toUpperCase() : section.name}
        bold
      />
      {section.sections.map((s) => {
        return (
          <BalanceReportSubsection section={s} country={country} key={s.name} />
        );
      })}
      <Report.TableRow
        title={
          transformToUpper ? section.sum.name.toUpperCase() : section.sum.name
        }
        values={RenderBalanceValues(section.sum)}
        marginBottom={country !== 'NO'}
        bold
      />
    </>
  );
}

function BalanceReportSuperSection({ section, country, transformToUpper }) {
  return (
    <>
      <Report.TableRow
        title={transformToUpper ? section.name.toUpperCase() : section.name}
        bold
      />
      {section.sections.map((s) => (
        <BalanceReportSection section={s} country={country} key={s.name} />
      ))}
      <Report.TableRow
        title={
          transformToUpper ? section.sum.name.toUpperCase() : section.sum.name
        }
        values={RenderBalanceValues(section.sum)}
        marginBottom
        bold
      />
    </>
  );
}

const RenderResultValues = (values) => [
  values.resultCurrentYear,
  values.resultCurrentYear,
  values.resultPreviousYear,
];

function ResultSection({ section, country }) {
  return section.accounts.length ? (
    <>
      <Report.TableRow
        title={section.name}
        values={country === 'NO' ? RenderResultValues(section.sum) : null}
        bold
      />
      <Report.ListSection country={country}>
        {section.accounts.map((account) => {
          return (
            <Report.TableRow
              number={account.id}
              title={account.name}
              values={RenderResultValues(account)}
              key={account.id}
            />
          );
        })}
      </Report.ListSection>
      {country !== 'NO' && (
        <Report.TableRow
          title={section.sum.name}
          values={RenderResultValues(section.sum)}
          marginBottom
          bold
        />
      )}
    </>
  ) : (
    ''
  );
}

function ResultItem({ item, country }) {
  switch (item.type) {
    case 'Row':
      return <Report.TableRow title={item.name} bold />;
    case 'Sum':
      return (
        <Report.TableRow
          title={item.name}
          values={RenderResultValues(item)}
          marginBottom={country !== 'NO'}
          bold
        />
      );
    case 'Section':
      return <ResultSection section={item} country={country} />;
    default:
      return null;
  }
}

export default function Reports({ client, t }) {
  const [fiscalYear, setFiscalYear] = useState('');
  const [reportPeriod, setReportPeriod] = useState('');

  const [accountReport, setAccountReport] = useState([]);
  const [fetchingAccountData, setFetchingAccountData] = useState(false);
  const targetRef = useRef();
  const [verticalMode, setVerticalMode] = useState(false);

  useEffect(() => {
    const getData = async () => {
      setFetchingAccountData(true);

      await AccountService.getAccountReport(client.id)
        .then((response) => {
          setAccountReport(response.data);

          const fromDate = `${Moment(response.data.fromDate).format(
            'YYYY-MM-DD'
          )}`;
          const toDate = `${Moment(response.data.toDate).format('YYYY-MM-DD')}`;
          const currentDate = Moment.utc().local();

          setFiscalYear(`${fromDate} - ${toDate}`);
          setReportPeriod(
            `${fromDate} - ${Moment(currentDate).format('YYYY-MM-DD')}`
          );
        })
        .catch(() => {})
        .finally(() => {
          setFetchingAccountData(false);
        });
    };
    if (client.externalConnectionActive) {
      getData();
    }
  }, [client]);

  const renderReports = () => (
    <div ref={targetRef}>
      <Module.ReportSection vertical={verticalMode}>
        <Report
          title={gT(t, '.report-title.balance-report')}
          client={client}
          fiscalYear={fiscalYear}
          period={reportPeriod}
          dateCreated={accountReport.dateCreated}
          t={t}
        >
          <Report.TableRow
            title=""
            values={[
              gT(t, '.table-row.values.ingoing-balance'),
              gT(t, '.table-row.values.ingoing-account-balance'),
              gT(t, '.table-row.values.period'),
              gT(t, '.table-row.values.outgoing-balance'),
            ]}
            type="text"
          />
          <Report.Divider />
          <BalanceReportSuperSection
            section={accountReport.balanceReport.assets}
            country={accountReport.country}
            transformToUpper
          />
          {accountReport.balanceReport.equityProvisionsAndDebt.type ===
          'Section' ? (
            <BalanceReportSection
              section={accountReport.balanceReport.equityProvisionsAndDebt}
              country={accountReport.country}
              transformToUpper
            />
          ) : (
            <BalanceReportSuperSection
              section={accountReport.balanceReport.equityProvisionsAndDebt}
              country={accountReport.country}
              transformToUpper
            />
          )}
          <Report.TableRow
            title={accountReport.balanceReport.sum.name.toUpperCase()}
            values={RenderBalanceValues(accountReport.balanceReport.sum)}
            bold
          />
        </Report>
        <Report
          title={gT(t, '.report-title.result-report')}
          client={client}
          fiscalYear={fiscalYear}
          period={reportPeriod}
          dateCreated={accountReport.dateCreated}
          t={t}
        >
          <Report.TableRow
            title=""
            values={[
              gT(t, '.table-row.values.period'),
              gT(t, '.table-row.values.accumulated'),
              gT(t, '.table-row.values.total-previous-year'),
            ]}
            type="text"
          />
          <Report.Divider />
          {accountReport.resultsReport.items.map((item) => (
            <ResultItem
              item={item}
              key={item.name}
              country={accountReport.country}
            />
          ))}
        </Report>
      </Module.ReportSection>
    </div>
  );

  return (
    <Module>
      {!client.externalConnectionActive ? (
        <p className="module-empty-text">
          {gT(t, '.module-empty-text.no-external-connection-active')}
        </p>
      ) : fetchingAccountData ? (
        <LoadingBar active status={gT(t, '.loading-bar')} relative />
      ) : Object.keys(accountReport).length === 0 ? (
        <p className="module-empty-text">
          {gT(t, '.module-empty-text.no-account-report')}
        </p>
      ) : (
        <>
          <Module.Nav>
            <div />
            <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
              <XpektorButton
                content={
                  verticalMode
                    ? gT(t, '.xpektor-btn.vertical')
                    : gT(t, '.xpektor-btn.side-by-side')
                }
                onClick={() => setVerticalMode(!verticalMode)}
              />
              <XpektorButton
                icon={faDownload}
                content={gT(t, '.xpektor-btn.save-as-pdf')}
                onClick={() =>
                  generatePDF(targetRef, {
                    filename: `${gT(t, '.pdf-file-name-prefix')}-${Moment.utc()
                      .local()
                      .format('YYYY-MM-DD')}-XPEKTOR.pdf`,
                  })
                }
              />
            </div>
          </Module.Nav>
          {renderReports()}
        </>
      )}
    </Module>
  );
}
